import { authenticatedInstance } from '../index/index.service';
import type {
  ClosedOrdersQuery,
  PaginatedClosedOrders,
  SessionFilterOption,
} from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type {
  ClosedOrderListItem,
  PaginatedClosedOrders,
  SessionFilterOption,
} from 'shared-models/browser';

class PastReceiptsService {
  // Summary: Manager past-receipts — paginated closed orders across all sessions (tenant-scoped).
  // Details: docs/function-library/web/services.md#past-receipts-service
  async listClosedOrders(
    tenantId: string,
    query: Partial<ClosedOrdersQuery>,
    employeeSession: string,
  ): Promise<PaginatedClosedOrders> {
    try {
      const response = await authenticatedInstance.get<PaginatedClosedOrders>(
        `/orders/tenant/${encodeURIComponent(tenantId)}/closed-orders`,
        {
          params: query,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to load closed orders'));
    }
  }

  // Summary: Bounded tenant-scoped session options for the past-receipts session filter dropdown.
  // Details: docs/function-library/web/services.md#past-receipts-service
  async listSessionFilterOptions(
    tenantId: string,
    employeeSession: string,
  ): Promise<SessionFilterOption[]> {
    try {
      const response = await authenticatedInstance.get<SessionFilterOption[]>(
        `/sessions/tenant/${encodeURIComponent(tenantId)}/filter-options`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to load sessions'));
    }
  }

  // Summary: Dashboard session filter-options without x-employee-session dependency.
  async listDashboardSessionFilterOptions(tenantId: string): Promise<SessionFilterOption[]> {
    try {
      const response = await authenticatedInstance.get<SessionFilterOption[]>(
        `/sessions/tenant/${encodeURIComponent(tenantId)}/dashboard-filter-options`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to load sessions'));
    }
  }
}

export const pastReceiptsService = new PastReceiptsService();
